import { useEffect, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { UserPlus, DollarSign, Smartphone, Zap, Megaphone } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { AppDownloadButtons } from "@/components/landing/AppDownloadButtons";
import { SocialProofTicker } from "@/components/landing/SocialProofTicker";
import { LazySection } from "@/components/ui/lazy-section";

// Lazy load heavy components
const EarningsCalculator = lazy(() => import("@/components/landing/EarningsCalculator").then(m => ({ default: m.EarningsCalculator })));
const Footer = lazy(() => import("@/components/layout/Footer").then(m => ({ default: m.Footer })));

export default function Index() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Store referral code if present
    const refCode = searchParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('referralCode', refCode);
    }
  }, [searchParams]);

  const handleGetStarted = () => {
    navigate("/auth");
  };

  const handleAdvertiser = () => {
    navigate("/brands");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-primary/5">
      {/* Skip to main content for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      
      <Header logoTo="/">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleAdvertiser} aria-label="View advertiser options">
            For Brands
          </Button>
          <Button onClick={handleGetStarted} aria-label="Sign in to your account">
            Sign In
          </Button>
        </div>
      </Header>

      <main id="main-content" role="main" className="flex-1" aria-label="Main content">
        {/* Social Proof Ticker */}
        <SocialProofTicker />

        {/* Hero Section - Enhanced */}
        <section 
          className="relative overflow-hidden bg-gradient-to-b from-background via-primary/5 to-background section-large" 
          aria-labelledby="hero-heading"
          style={{ contentVisibility: 'auto' }}
        >
          {/* Static Background */}
          <div className="absolute inset-0 gradient-hero opacity-10 transition-opacity duration-1000" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
          
          <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 text-center space-y-3 sm:space-y-4 md:space-y-5 lg:space-y-6 relative">
            <h1 id="hero-heading" className="text-display">
              Turn Your Phone Into a <span className="text-gradient-primary">Paycheck</span>
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Earn extra income in your spare time with sponsored notifications. Get paid for opening brand pings—simple actions, real rewards.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4">
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                className="w-full sm:w-auto min-w-[200px] text-base md:text-lg h-12 px-8 shadow-glow hover:shadow-elevated hover-elevate"
                aria-label="Start earning money as a user"
              >
                <Smartphone className="mr-2 h-5 w-5" aria-hidden="true" />
                Start Earning (Users)
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                onClick={handleAdvertiser}
                className="w-full sm:w-auto min-w-[200px] text-base md:text-lg h-12 px-8"
                aria-label="Learn about advertising your brand"
              >
                <Megaphone className="mr-2 h-5 w-5" aria-hidden="true" />
                Advertise Your Brand
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 justify-center items-center pt-3 sm:pt-4 md:pt-6 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" width="16" height="16">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                No credit card required
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" width="16" height="16">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Withdraw anytime
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" width="16" height="16">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free to join
              </span>
            </div>
          </div>
        </section>
        {/* How It Works Section */}
        <section className="bg-gradient-to-b from-secondary/20 via-primary/10 to-background border-b border-primary/10 section-standard" aria-labelledby="how-it-works-heading" role="region">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6">
            <h2 id="how-it-works-heading" className="heading-2 text-center mb-8 sm:mb-10 md:mb-12">
              How It Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-10 md:mb-12 stagger-fade-in">
              <div className="flex flex-col items-center text-center p-4 sm:p-6 rounded-lg glass-card-enhanced hover-elevate group">
                <div className="bg-gradient-to-br from-primary/20 to-primary/10 p-3 sm:p-4 rounded-full mb-3 sm:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">
                  <UserPlus className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">1. Sign Up</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Create your free account in seconds
                </p>
              </div>

              <div className="flex flex-col items-center text-center p-4 sm:p-6 rounded-lg glass-card-enhanced hover-elevate group">
                <div className="bg-gradient-to-br from-primary/20 to-primary/10 p-3 sm:p-4 rounded-full mb-3 sm:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">
                  <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">2. Get Pings</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Receive notifications based on your interests
                </p>
              </div>

              <div className="flex flex-col items-center text-center p-4 sm:p-6 rounded-lg glass-card-enhanced hover-elevate group">
                <div className="bg-gradient-to-br from-success/20 to-success/10 p-3 sm:p-4 rounded-full mb-3 sm:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">
                  <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-success" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">3. Earn Money</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Get paid for every notification you receive
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Earnings Calculator Section */}
        <LazySection fallback={<div className="h-96" aria-label="Loading earnings calculator" />}>
          <Suspense fallback={<div className="h-96" aria-label="Loading earnings calculator" />}>
            <section className="bg-muted/20 border-y border-primary/10 section-standard" aria-labelledby="calculator-heading" role="region">
              <EarningsCalculator />
            </section>
          </Suspense>
        </LazySection>

        {/* Final CTA */}
        <section className="py-8 sm:py-12 bg-gradient-to-r from-primary via-secondary to-primary text-primary-foreground border-b border-primary-foreground/10 safe-bottom" role="region">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 text-center">
            <AppDownloadButtons variant="default" className="justify-center" />
          </div>
        </section>
      </main>

      <LazySection fallback={<div className="h-64" aria-label="Loading footer" />}>
        <Suspense fallback={<div className="h-64" aria-label="Loading footer" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
}
